import { useEffect, useState, useMemo } from "react";
import { Search, Download, Filter, ChevronDown, ChevronUp, Copy, Check, Tag as TagIcon, X, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/app/AdminLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TagInput from "@/components/app/TagInput";
import api from "@/lib/api";

// All attribution fields that the server may persist for a lead (in display order).
const DETAIL_FIELDS = [
  // === Routing ===
  { key: "id", label: "Lead ID", group: "routing" },
  { key: "campaign_id", label: "Campaign ID", group: "routing" },
  { key: "affiliate_id", label: "Affiliate ID", group: "routing" },
  { key: "list_id", label: "List ID", group: "routing" },
  { key: "webhook_status", label: "Webhook Status", group: "routing" },
  // === UTMs ===
  { key: "utm_source", label: "UTM Source", group: "utm" },
  { key: "utm_medium", label: "UTM Medium", group: "utm" },
  { key: "utm_campaign", label: "UTM Campaign", group: "utm" },
  { key: "utm_term", label: "UTM Term", group: "utm" },
  { key: "utm_content", label: "UTM Content", group: "utm" },
  // === Click IDs (for manual conversion APIs) ===
  { key: "fbclid", label: "fbclid (Facebook)", group: "clicks" },
  { key: "gclid", label: "gclid (Google)", group: "clicks" },
  { key: "ttclid", label: "ttclid (TikTok)", group: "clicks" },
  { key: "msclkid", label: "msclkid (Bing)", group: "clicks" },
  { key: "twclid", label: "twclid (X/Twitter)", group: "clicks" },
  { key: "li_fat_id", label: "li_fat_id (LinkedIn)", group: "clicks" },
  { key: "wbraid", label: "wbraid (Google iOS)", group: "clicks" },
  { key: "gbraid", label: "gbraid (Google iOS)", group: "clicks" },
  // === Meta CAPI cookies ===
  { key: "fbp", label: "_fbp (Meta browser)", group: "meta" },
  { key: "fbc", label: "_fbc (Meta click)", group: "meta" },
  // === Context ===
  { key: "landing_url", label: "Landing URL", group: "context" },
  { key: "referrer", label: "Referrer", group: "context" },
  { key: "user_agent", label: "User Agent", group: "context" },
  { key: "screen_resolution", label: "Screen", group: "context" },
  { key: "language", label: "Language", group: "context" },
  { key: "ip_address", label: "IP Address", group: "context" },
];

const GROUP_LABELS = {
  routing: "ניתוב",
  utm: "UTM Parameters",
  clicks: "Click IDs (להמרה ידנית)",
  meta: "Meta CAPI Cookies",
  context: "פרטי גולש",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [search, setSearch] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [affiliateId, setAffiliateId] = useState("");
  const [tagFilter, setTagFilter] = useState("");  // single-tag OR filter
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkTagOpen, setBulkTagOpen] = useState(false);
  const [bulkTags, setBulkTags] = useState([]);

  const loadTags = async () => {
    try {
      const { data } = await api.get("/admin/tags");
      setAllTags(data.map((r) => r.tag));
    } catch (err) { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [c, a] = await Promise.all([api.get("/campaigns"), api.get("/affiliates")]);
        setCampaigns(c.data);
        setAffiliates(a.data);
        await loadTags();
      } catch (err) {
        console.error("Failed to load filters:", err);
      }
    })();
  }, []);

  const affiliateById = useMemo(() => {
    const map = {};
    affiliates.forEach((a) => { map[a.id] = a; });
    return map;
  }, [affiliates]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (campaignId && campaignId !== "ALL") params.set("campaign_id", campaignId);
      if (tagFilter && tagFilter !== "ALL") params.set("tags_or", tagFilter);
      const { data } = await api.get(`/leads?${params.toString()}`);
      let filtered = data;
      if (affiliateId === "DIRECT") {
        filtered = data.filter((l) => !l.affiliate_id);
      } else if (affiliateId && affiliateId !== "ALL") {
        filtered = data.filter((l) => l.affiliate_id === affiliateId);
      }
      setLeads(filtered);
      setSelectedIds(new Set());
    } catch (err) {
      console.error("Failed to load leads:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [campaignId, affiliateId, tagFilter]);

  const exportCsv = () => {
    if (leads.length === 0) return;
    const header = [
      "created_at", "id", "name", "phone", "email", "tags",
      "campaign_id", "affiliate_id", "list_id", "affiliate_name", "webhook_status",
      ...DETAIL_FIELDS.filter((f) => !["id", "campaign_id", "affiliate_id", "list_id", "webhook_status"].includes(f.key)).map((f) => f.key),
    ];
    const rows = leads.map((l) => header.map((h) => {
      let val;
      if (h === "affiliate_name") val = affiliateById[l.affiliate_id]?.name || "";
      else if (h === "tags") val = (l.tags || []).join("|");
      else val = l[h] ?? "";
      return `"${String(val).replace(/"/g, '""')}"`;
    }).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyLeadJson = async (lead) => {
    const aff = affiliateById[lead.affiliate_id];
    const payload = { ...lead, affiliate_name: aff?.name || null };
    const json = JSON.stringify(payload, null, 2);
    try {
      await navigator.clipboard.writeText(json);
      setCopiedId(lead.id);
      toast.success("JSON הועתק ללוח");
      setTimeout(() => setCopiedId(null), 1500);
    } catch (err) {
      console.error("clipboard failed", err);
      toast.error("העתקה נכשלה — דפדפן ישן?");
    }
  };

  const toggleExpand = (id) => setExpandedId((cur) => (cur === id ? null : id));

  const toggleSelect = (id) => setSelectedIds((cur) => {
    const n = new Set(cur);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const selectAll = () => setSelectedIds(new Set(leads.map((l) => l.id)));
  const clearSelection = () => setSelectedIds(new Set());

  const updateLeadTags = (leadId, tags) => {
    setLeads((cur) => cur.map((l) => (l.id === leadId ? { ...l, tags } : l)));
  };

  const addTagsToLead = async (leadId, tags) => {
    try {
      const { data } = await api.post(`/admin/leads/${leadId}/tags`, { tags });
      updateLeadTags(leadId, data.tags);
      loadTags();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "שגיאה בתיוג");
    }
  };

  const removeTagFromLead = async (leadId, tag) => {
    try {
      const { data } = await api.delete(`/admin/leads/${leadId}/tags/${encodeURIComponent(tag)}`);
      updateLeadTags(leadId, data.tags);
      loadTags();
    } catch (err) {
      toast.error("שגיאה בהסרת תיוג");
    }
  };

  const deleteLead = async (leadId, email) => {
    if (!window.confirm(`למחוק את הליד ${email}?\nהאימייל יתווסף לרשימת ההשתקה (SHA-256 hash) ואף מייל לא יישלח אליו יותר.`)) return;
    try {
      await api.delete(`/admin/leads/${leadId}`);
      toast.success("הליד נמחק ונוסף להשתקות");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "שגיאה במחיקה");
    }
  };

  const backfillMagicTokens = async () => {
    if (!window.confirm(
      "יצירת Magic Link Tokens לכל הלידים שאין להם עדיין (מאפשר להם להיכנס ל-VOD).\n" +
      "פעולה בטוחה — לידים שכבר יש להם token יידלגו. להמשיך?"
    )) return;
    try {
      const { data } = await api.post("/admin/leads/backfill-magic-tokens?only_missing=true");
      toast.success(`עודכנו ${data.updated} מתוך ${data.scanned} לידים · דולגו ${data.skipped}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "שגיאה");
    }
  };

  const bulkAdd = async () => {
    if (bulkTags.length === 0) return toast.error("בחר תיוגים");
    if (selectedIds.size === 0) return toast.error("בחר לידים");
    try {
      const { data } = await api.post("/admin/leads/bulk-tag", {
        lead_ids: Array.from(selectedIds),
        tags: bulkTags,
        op: "add",
      });
      toast.success(`עודכנו ${data.modified} לידים`);
      setBulkTagOpen(false);
      setBulkTags([]);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "שגיאה");
    }
  };

  return (
    <AdminLayout title="לידים">
      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 border border-stone-200 mb-5">
        <div className="grid md:grid-cols-12 gap-3 items-center">
          <div className="md:col-span-3 relative">
            <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="חיפוש שם / אימייל / טלפון"
              data-testid="search-input"
              className="h-11 pr-9"
            />
          </div>
          <div className="md:col-span-3">
            <Select value={campaignId || "ALL"} onValueChange={(v) => setCampaignId(v === "ALL" ? "" : v)}>
              <SelectTrigger className="h-11" data-testid="filter-campaign"><SelectValue placeholder="כל הקמפיינים" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">כל הקמפיינים</SelectItem>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.brand_name} (/{c.slug})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Select value={affiliateId || "ALL"} onValueChange={(v) => setAffiliateId(v === "ALL" ? "" : v)}>
              <SelectTrigger className="h-11" data-testid="filter-affiliate"><SelectValue placeholder="אפיליאייט" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">כל האפיליאייטים</SelectItem>
                <SelectItem value="DIRECT">ישיר (ללא אפיליאייט)</SelectItem>
                {affiliates.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Select value={tagFilter || "ALL"} onValueChange={(v) => setTagFilter(v === "ALL" ? "" : v)}>
              <SelectTrigger className="h-11" data-testid="filter-tag"><SelectValue placeholder="תיוג" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">כל התיוגים</SelectItem>
                {allTags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 flex gap-2">
            <Button onClick={load} className="flex-1 h-11 bg-brand-charcoal text-white rounded-xl"><Filter className="w-4 h-4 me-2" />סנן</Button>
            <Button onClick={exportCsv} variant="outline" className="h-11 rounded-xl" data-testid="export-csv-btn" title="ייצוא ל-CSV">
              <Download className="w-4 h-4" />
            </Button>
            <Button onClick={backfillMagicTokens} variant="outline" className="h-11 rounded-xl" data-testid="backfill-magic-btn" title="Backfill Magic Link Tokens">
              <KeyRound className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="mt-3 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="bulk-bar">
            <div className="text-sm">
              <span className="font-bold">{selectedIds.size}</span> לידים נבחרו
              <button onClick={clearSelection} className="ms-3 text-xs text-stone-500 hover:underline">נקה</button>
            </div>
            <div className="flex gap-2">
              {!bulkTagOpen ? (
                <Button size="sm" onClick={() => setBulkTagOpen(true)} data-testid="bulk-tag-btn" className="rounded-full bg-brand-charcoal text-white">
                  <TagIcon className="w-4 h-4 me-1" />תיוג המוני
                </Button>
              ) : (
                <div className="flex items-center gap-2 flex-1">
                  <div className="flex-1 min-w-[220px]">
                    <TagInput value={bulkTags} onChange={setBulkTags} suggestions={allTags} placeholder="תיוגים לצירוף..." testId="bulk-tag-input" compact />
                  </div>
                  <Button size="sm" onClick={bulkAdd} data-testid="bulk-tag-apply" className="bg-emerald-600 text-white rounded-full">החל</Button>
                  <Button size="sm" variant="outline" onClick={() => { setBulkTagOpen(false); setBulkTags([]); }} className="rounded-full">ביטול</Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr className="text-right">
                <th className="w-10 px-2 py-3">
                  <input
                    type="checkbox"
                    checked={leads.length > 0 && selectedIds.size === leads.length}
                    onChange={(e) => (e.target.checked ? selectAll() : clearSelection())}
                    data-testid="select-all-checkbox"
                    className="rounded"
                  />
                </th>
                <th className="w-8 px-2 py-3" />
                {["תאריך", "שם", "אימייל", "טלפון", "תיוגים", "אפיליאייט", "Source", "Webhook"].map((h) => (
                  <th key={h} className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="py-12 text-center text-stone-500">טוען...</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={10} className="py-12 text-center text-stone-500">אין לידים תואמים.</td></tr>
              ) : (
                leads.map((l) => {
                  const aff = affiliateById[l.affiliate_id];
                  const isOpen = expandedId === l.id;
                  return (
                    <ExpandableRow
                      key={l.id}
                      lead={l}
                      affiliate={aff}
                      isOpen={isOpen}
                      onToggle={() => toggleExpand(l.id)}
                      onCopy={() => copyLeadJson(l)}
                      copied={copiedId === l.id}
                      selected={selectedIds.has(l.id)}
                      onToggleSelect={() => toggleSelect(l.id)}
                      onAddTags={(tags) => addTagsToLead(l.id, tags)}
                      onRemoveTag={(t) => removeTagFromLead(l.id, t)}
                      onDelete={() => deleteLead(l.id, l.email)}
                      allTags={allTags}
                    />
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

function ExpandableRow({ lead, affiliate, isOpen, onToggle, onCopy, copied, selected, onToggleSelect, onAddTags, onRemoveTag, onDelete, allTags }) {
  return (
    <>
      <tr className="border-b border-stone-100 hover:bg-stone-50" data-testid={`lead-${lead.id}`}>
        <td className="px-2 py-3 text-center">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            data-testid={`select-${lead.id}`}
            className="rounded"
          />
        </td>
        <td className="px-2 py-3 text-center">
          <button
            type="button"
            onClick={onToggle}
            data-testid={`lead-expand-${lead.id}`}
            className="w-7 h-7 rounded-lg hover:bg-stone-200 flex items-center justify-center transition"
            aria-label={isOpen ? "סגירת פרטים" : "הצגת פרטים"}
          >
            {isOpen ? <ChevronUp className="w-4 h-4 text-stone-600" /> : <ChevronDown className="w-4 h-4 text-stone-600" />}
          </button>
        </td>
        <td className="px-4 py-3 text-xs text-stone-500 whitespace-nowrap">{new Date(lead.created_at).toLocaleString("he-IL")}</td>
        <td className="px-4 py-3 font-bold">{lead.name}</td>
        <td className="px-4 py-3 ltr text-left">{lead.email}</td>
        <td className="px-4 py-3 ltr text-left">{lead.phone}</td>
        <td className="px-4 py-3 max-w-[220px]" data-testid={`lead-tags-${lead.id}`}>
          {(lead.tags || []).length === 0 ? (
            <span className="text-xs text-stone-300">—</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {(lead.tags || []).slice(0, 3).map((t) => (
                <span key={t} className="inline-flex items-center gap-0.5 text-[10px] font-bold rounded-full bg-amber-50 border border-amber-200 text-brand-amber px-1.5 py-0.5" data-testid={`row-tag-${lead.id}-${t}`}>
                  {t}
                  <button type="button" onClick={() => onRemoveTag(t)} className="hover:bg-amber-100 rounded-full">
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
              {lead.tags.length > 3 && <span className="text-[10px] text-stone-400">+{lead.tags.length - 3}</span>}
            </div>
          )}
        </td>
        <td className="px-4 py-3" data-testid={`lead-aff-${lead.id}`}>
          {affiliate ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-full bg-amber-50 text-brand-amber border border-amber-200">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-amber" />
              {affiliate.name}
            </span>
          ) : lead.affiliate_id ? (
            <span className="text-xs text-stone-400">אפיליאייט מחוק</span>
          ) : (
            <span className="text-xs text-stone-400">ישיר</span>
          )}
        </td>
        <td className="px-4 py-3 text-stone-600 text-xs">
          {lead.utm_source || "—"}
          {lead.utm_campaign && <span className="block text-stone-400">{lead.utm_campaign}</span>}
        </td>
        <td className="px-4 py-3">
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
            lead.webhook_status === "ok" ? "bg-emerald-100 text-emerald-700" :
            lead.webhook_status === "skipped" ? "bg-stone-100 text-stone-500" :
            "bg-red-100 text-red-700"
          }`}>
            {lead.webhook_status}
          </span>
        </td>
      </tr>
      {isOpen && (
        <tr className="bg-stone-50/70" data-testid={`lead-details-${lead.id}`}>
          <td colSpan={10} className="px-4 py-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-[800] text-stone-700">פרטי attribution מלאים</h4>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={onCopy}
                  data-testid={`lead-copy-${lead.id}`}
                  className="h-9 rounded-full bg-brand-charcoal text-white text-xs px-4 gap-2"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "הועתק!" : "העתק JSON"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onDelete}
                  data-testid={`lead-delete-${lead.id}`}
                  className="h-9 rounded-full text-red-600 border-red-200 hover:bg-red-50 text-xs px-4 gap-2"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  מחק (+השתקה)
                </Button>
              </div>
            </div>
            {/* Tag editor */}
            <div className="bg-white rounded-xl border border-stone-200 p-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <TagIcon className="w-4 h-4 text-brand-amber" />
                <h5 className="font-[800] text-xs uppercase tracking-wider text-stone-500">תיוגים</h5>
              </div>
              <TagInput
                value={lead.tags || []}
                onChange={(next) => {
                  const cur = new Set(lead.tags || []);
                  const nxt = new Set(next);
                  const added = next.filter((t) => !cur.has(t));
                  const removed = (lead.tags || []).filter((t) => !nxt.has(t));
                  if (added.length > 0) onAddTags(added);
                  removed.forEach((t) => onRemoveTag(t));
                }}
                suggestions={allTags}
                placeholder="הוסף תיוג..."
                testId={`lead-tag-editor-${lead.id}`}
              />
            </div>
            <DetailGrid lead={lead} affiliate={affiliate} />
          </td>
        </tr>
      )}
    </>
  );
}

function DetailGrid({ lead, affiliate }) {
  const grouped = useMemo(() => {
    const g = { routing: [], utm: [], clicks: [], meta: [], context: [] };
    DETAIL_FIELDS.forEach((f) => g[f.group].push(f));
    return g;
  }, []);

  return (
    <div className="grid md:grid-cols-2 gap-4">
      {Object.keys(grouped).map((groupKey) => {
        const fields = grouped[groupKey];
        const hasAnyValue = fields.some((f) => lead[f.key] != null && lead[f.key] !== "");
        return (
          <div key={groupKey} className="bg-white rounded-xl border border-stone-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h5 className="font-[800] text-xs uppercase tracking-wider text-stone-500">{GROUP_LABELS[groupKey]}</h5>
              {!hasAnyValue && <span className="text-[10px] text-stone-400">ריק</span>}
            </div>
            <dl className="space-y-2">
              {fields.map((f) => {
                let value = lead[f.key];
                if (f.key === "affiliate_id" && value && affiliate) {
                  value = `${value} · ${affiliate.name}`;
                }
                return (
                  <div key={f.key} className="grid grid-cols-3 gap-2 items-start">
                    <dt className="text-xs text-stone-500 font-bold">{f.label}</dt>
                    <dd className="col-span-2 text-xs ltr text-left break-all">
                      {value ? (
                        <span className="text-stone-800">{String(value)}</span>
                      ) : (
                        <span className="text-stone-300">—</span>
                      )}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </div>
        );
      })}
    </div>
  );
}
